import * as React from 'react';
import { capitalizeFirstLetter, getBaseUrl } from '../../../utilities';
import styled from 'styled-components';
import { rem } from 'polished';
import { Link } from 'react-router';
import ImagePlaceholder from '../Image/ImagePlaceholder';
import { ChematCustomerType } from '../../../library/ChematCustomerType';
import { __ } from '@bart.sk-eshop/react-i18n';

const NEAR_BLACK = '#171717';

const Wrapper = styled.ul`
  margin: 0 0 ${rem(16)};
  padding: 0;
  list-style: none;
  display: grid;
  gap: ${rem(8)};
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-height: ${rem(1)}; /* Prevent CLS by reserving minimal space */
  ${({ theme }) => theme.media('sm')} {
    gap: ${rem(16)};
    grid-template-columns: repeat(auto-fill, minmax(${rem(180)}, 1fr));
  }
`;

const Item = styled.li`
  list-style: none;
  display: flex; /* let the card link fill the (stretched) grid cell height */
`;

const ItemLink = styled(Link)`
  flex: 1 1 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(4)};
  min-height: ${rem(56)};
  padding: ${rem(4)};
  background: ${({ theme }) => theme.color.white};
  border: ${rem(1)} solid #e0e0e0;
  border-radius: ${rem(8)};
  box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.05);
  color: ${NEAR_BLACK};
  text-decoration: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: ${NEAR_BLACK};
    text-decoration: none;
    border-color: #d6d6d6;
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(0, 0, 0, 0.1);
  }
  ${({ theme }) => theme.media('sm')} {
    gap: ${rem(8)};
    padding: ${rem(4)} ${rem(12)} ${rem(4)} ${rem(4)};
  }
`;

const ItemImage = styled(ImagePlaceholder)`
  width: ${rem(40)};
  height: ${rem(40)};
  flex: 0 0 auto;
  ${({ theme }) => theme.media('sm')} {
    width: ${rem(48)};
    height: ${rem(48)};
  }
`;

const ItemName = styled.p`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: ${rem(15)};
  font-weight: 600;
  line-height: ${rem(18)};
  color: ${NEAR_BLACK};
  ${({ theme }) => theme.media('sm')} {
    line-height: ${rem(20)};
  }
`;

interface categoryListMobileProps {
  categories: any[];
  user: any;
}

const CategoryList = (props: categoryListMobileProps) => {
  const { categories, user } = props;

  const placeholder = '/images/placeholder-100.png';
  const placeholder2x = '/images/placeholder-100@2x.png';

  const filteredCategories = React.useMemo(() => {
    if (!categories?.length) return [];

    return categories.filter((item) => {
      if (
        (!user || user.skvelko_type === ChematCustomerType.RETAIL) &&
        item.category_number === 'KAT_SEASON'
      ) {
        return false;
      }

      if (
        user &&
        user.skvelko_type !== ChematCustomerType.RETAIL &&
        item.category_number === 'KAT_ACTION'
      ) {
        return false;
      }

      return true;
    });
  }, [categories, user]);

  return (
    <>
      {filteredCategories.length ? (
        <Wrapper>
          {filteredCategories.map((item, i) => {
            return (
              <Item key={i}>
                <ItemLink to={getBaseUrl(item?.url)}>
                  <ItemImage
                    src={item?.image}
                    placeholder={placeholder}
                    retinaPlaceholder={placeholder2x}
                    w={48}
                    h={48}
                    alt={
                      __('Kategória') +
                      ' ' +
                      capitalizeFirstLetter(item?.category_name)
                    }
                  />
                  <ItemName>
                    {capitalizeFirstLetter(item?.category_name)}
                  </ItemName>
                </ItemLink>
              </Item>
            );
          })}
        </Wrapper>
      ) : (
        ''
      )}
    </>
  );
};

// OPTIMALIZÁCIA: Memoizuj komponent aby sa re-renderoval len pri zmene categories alebo user
export default React.memo(CategoryList, (prevProps, nextProps) => {
  return (
    prevProps.categories === nextProps.categories &&
    prevProps.user?.skvelko_type === nextProps.user?.skvelko_type
  );
});
